import { ErrorLink } from "@apollo/client/link/error";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import type { ServerError } from "@apollo/client";
import { trackException, trackEvent } from "@/telemetry/ai";

// Deduplicate error logs to prevent console spam in dev mode
// (React 18 StrictMode + cache-and-network can cause repeated logs)
const recentErrors = new Map<string, number>();
const ERROR_WINDOW_MS = 3000;

const shouldLogError = (key: string): boolean => {
  const now = Date.now();
  const expiry = recentErrors.get(key) ?? 0;
  if (now < expiry) return false;
  recentErrors.set(key, now + ERROR_WINDOW_MS);
  return true;
};

/**
 * Error link that handles GraphQL and network errors.
 * Tags errors with the Assistant service for better telemetry.
 *
 * On 401 network errors, clears token cache and retries once.
 */
export const errorLink = new ErrorLink(({ error, operation, forward }) => {
  const service = "Assistant";
  const opName = operation?.operationName ?? "unknown";

  // Handle GraphQL errors (combined in v4)
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach((gqlError) => {
      const { message, locations, path } = gqlError;
      const key = `gql:${service}:${opName}:${message}`;
      if (shouldLogError(key)) {
        console.error(`[${service} GraphQL error in ${opName}]: ${message}`, {
          locations,
          path,
        });
      }

      trackEvent("GraphQLError", {
        service,
        operation: opName,
        message,
        path: path?.join("."),
      });
    });
  } else {
    // Handle network/other errors
    const err = error as Partial<ServerError> & Error;

    // Add a lightweight retry guard on the operation to avoid infinite loops
    const retryKey = "__auth_retry_attempted__";
    const alreadyRetried = Boolean(operation.getContext()[retryKey]);

    // On 401, retry once with the same token.
    // MSAL will handle token refresh on the next acquireTokenSilent call.
    if ((err as ServerError).statusCode === 401 && !alreadyRetried) {
      operation.setContext({ [retryKey]: true });
      return forward(operation);
    }

    // Do NOT retry on 405 (method not allowed) - treat as non-auth server error
    if ((err as ServerError).statusCode === 405) {
      const key405 = `net:${service}:${opName}:405`;
      if (shouldLogError(key405)) {
        console.error(`[${service} Network 405 in ${opName}]: method not allowed - not retrying auth`, error);
      }
      trackException(err, {
        service,
        operation: opName,
        type: "NetworkError",
        statusCode: 405,
      });
      return; // stop processing; don't retry
    }

    const key = `net:${service}:${opName}:${(err as ServerError).statusCode ?? ""}`;
    if (shouldLogError(key)) {
      console.error(`[${service} Network error in ${opName}]:`, error);
    }

    trackException(err, {
      service,
      operation: opName,
      type: "NetworkError",
      statusCode: (err as ServerError).statusCode,
    });
  }
});
